import React, { useEffect, useState } from "react";
import "../styles/AltinSlider.css";
import { useWebSocket } from "../hooks/useWebSocket";
import Reveal from "./Reveal";
import { FaCoins, FaDollarSign, FaExchangeAlt, FaUserCircle, FaShieldAlt, FaWifi, FaChartLine, FaMobileAlt, FaClock, FaWallet } from "react-icons/fa";
import AmblemLight from "../assets/images/amblem_light.png";

const DOVIZ_SUMMARY_CODES = ["USDTRL", "EURTRL", "EURUSD"];
const menuItems = [
  { label: "ALTIN", icon: <FaCoins color="#A7925B" size={32} /> },
  { label: "DOVIZ", icon: <FaDollarSign color="#3b82f6" size={32} />, active: true },
  { label: "CEVIRI", icon: <FaExchangeAlt color="#22c55e" size={32} /> },
  { label: "PORTFÖY", icon: <FaUserCircle color="#a855f7" size={32} /> },
];

const leftInfos = [
  {
    icon: <FaShieldAlt color="#A7925B" size={28} />, title: "Güvenli", desc: "Fiyatlarımıza en güvenli şekilde ulaşın."
  },
  {
    icon: <FaWifi color="#3b82f6" size={28} />, title: "Erişilebilirlik", desc: "Her zaman, her yerde fiyatlarımızı takip edebilirsiniz."
  },
  {
    icon: <FaChartLine color="#22c55e" size={28} />, title: "Kârlılık", desc: "Güncel fiyatlar ile kazancınızı maksimize edin"
  },
];
const rightInfos = [
  {
    icon: <FaMobileAlt color="#A7925B" size={28} />, title: "Mobil Uygulama", desc: "Mobil uygulamamız ile her zaman yanınızdayız."
  },
  {
    icon: <FaClock color="#3b82f6" size={28} />, title: "Zaman", desc: "Telefon görüşmeleri ile zaman kaybetmeyin ve fiyat kaçırmayın."
  },
  {
    icon: <FaWallet color="#a855f7" size={28} />, title: "PORTFÖY", desc: "Güncel fiyatlar ile kazancınızı en üst düzeye taşıyın."
  },
];

const AltinSlider: React.FC = () => {
  const { data: prices, connectionStatus } = useWebSocket("wss://sslsocket.aifanet.com/5.250.255.84/12254");

  // Önceki fiyatları saklamak için state
  const [prevPrices, setPrevPrices] = useState<{ [code: string]: number }>({});
  // Fiyat yönlerini saklamak için state
  const [priceDirections, setPriceDirections] = useState<{ [code: string]: "up" | "down" | null }>({});

  // Fiyatlar değiştiğinde yönleri hesapla
  useEffect(() => {
    const newDirections: { [code: string]: "up" | "down" | null } = {};
    const newPrevPrices: { [code: string]: number } = { ...prevPrices };
    prices.forEach((row) => {
      const prev = prevPrices[row.currencyPair];
      if (prev === undefined) {
        newDirections[row.currencyPair] = null; // İlk veri, yön yok
      } else if (row.sellRate > prev) {
        newDirections[row.currencyPair] = "up";
      } else if (row.sellRate < prev) {
        newDirections[row.currencyPair] = "down";
      } else {
        newDirections[row.currencyPair] = null;
      }
      newPrevPrices[row.currencyPair] = row.sellRate;
    });
    setPriceDirections(newDirections);
    setPrevPrices(newPrevPrices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices]);

  // DOVIZ kategorisi için özet kutular
  const summaryData = DOVIZ_SUMMARY_CODES.map((code) => {
    const item = prices.find((p) => p.currencyPair === code);
    return {
      code,
      value: item ? item.sellRate.toLocaleString("tr-TR", { minimumFractionDigits: 3 }) : "-",
      change: "-",
    };
  });

  // Tablo verisi: Sadece DOVIZ kategorisindekiler
  const dovizRows = prices
    .filter((row) => row.type === "DOVIZ")
    .map((row) => ({
      code: row.currencyPair,
      bid: row.buyRate !== undefined ? row.buyRate.toLocaleString("tr-TR", { minimumFractionDigits: 3 }) : "-",
      ask: row.sellRate !== undefined ? row.sellRate.toLocaleString("tr-TR", { minimumFractionDigits: 3 }) : "-",
      up: priceDirections[row.currencyPair] === "up",
      down: priceDirections[row.currencyPair] === "down",
      change: "-",
      time: new Date().toLocaleTimeString("tr-TR"),
    }));

  const visibleRows = dovizRows.slice(0, 10);
  const hasMoreRows = dovizRows.length > 10;

  return (
    <div className="altin-slider-main-flex">
      {/* Sol kutu */}
      <Reveal variant="fade-right" duration={700} className="altin-slider-sidebox altin-slider-sidebox-left">
        {leftInfos.map((info, i) => (
          <div className="altin-slider-infobox" key={info.title} style={{ transitionDelay: `${i * 100}ms` }}>
            <div className="altin-slider-infoicon">{info.icon}</div>
            <div>
              <div className="altin-slider-infotitle">{info.title}</div>
              <div className="altin-slider-infodesc">{info.desc}</div>
            </div>
          </div>
        ))}
      </Reveal>
      {/* Telefon mockup */}
      <Reveal variant="zoom" duration={750} delay={100} className="altin-slider-centerbox">
        <div className="altin-slider-phone-mockup">
          {/* Üstte logo ve özet fiyat kutuları */}
          <div className="slider-header-row">
            <div className="slider-logo-group" style={{ justifyContent: 'center', width: '100%' }}>
              <img src={AmblemLight} alt="HG Amblem" style={{ height: 48, width: 48, objectFit: 'contain', borderRadius: '50%', border: '2px solid #89764b', background: '#fff', boxShadow: '0 2px 8px #0001' }} />
            </div>
          </div>
          <div className="slider-summary-row">
            {summaryData.map((item) => (
              <div className="slider-summary-box" key={item.code}>
                <div className="slider-summary-code">{item.code}</div>
                <div className="slider-summary-value">{item.value}</div>
                <div className="slider-summary-change">{item.change}</div>
              </div>
            ))}
          </div>
          {/* Tablo */}
          <div className="slider-table-wrapper" style={{ maxHeight: '370px', overflowY: hasMoreRows ? 'auto' : 'visible' }}>
            <table className="slider-table">
              <thead>
                <tr>
                  <th>BİRİM</th>
                  <th>ALIŞ</th>
                  <th>SATIŞ</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.code} className={row.up ? "slider-row-up" : row.down ? "slider-row-down" : ""}>
                    <td>
                      <div className="slider-table-code">{row.code}</div>
                      <div className="slider-table-time">{row.time}</div>
                    </td>
                    <td className="slider-table-bid">{row.bid}</td>
                    <td className="slider-table-ask">{row.ask}</td>
                    <td>
                      {row.up && <span className="slider-arrow-up">▲</span>}
                      {row.down && <span className="slider-arrow-down">▼</span>}
                      <span className="slider-table-change">{row.change}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Bilgi satırı */}
          <div className="slider-info-row">Fiyatlarımız bilgi amaçlıdır. ({connectionStatus})</div>
          {/* Menü */}
          <div className="slider-menu-row">
            {menuItems.map((item) => (
              <div className={`slider-menu-item${item.active ? " slider-menu-item-active" : ""}`} key={item.label}>
                {item.icon}
                <div className="slider-menu-label">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
      {/* Sağ kutu */}
      <Reveal variant="fade-left" duration={700} delay={200} className="altin-slider-sidebox altin-slider-sidebox-right">
        {rightInfos.map((info, i) => (
          <div className="altin-slider-infobox" key={info.title} style={{ transitionDelay: `${i * 100}ms` }}>
            <div className="altin-slider-infoicon">{info.icon}</div>
            <div>
              <div className="altin-slider-infotitle">{info.title}</div>
              <div className="altin-slider-infodesc">{info.desc}</div>
            </div>
          </div>
        ))}
      </Reveal>
    </div>
  );
};

export default AltinSlider; 